import {
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import { useEffect, useState } from 'react';
import Backend from '../backend/Backend';
import { UserRole } from '../common/types';
import AquantCancelIcon from '../resources/images/aq-cancel.svg';
import Util from '../Util';
import './CloneProjectDialog.scss';

const CloneProjectDialog = (props: {
    handleClose: Function;
    handleSubmit: Function;
    getAvailableTenants: Function;
}) => {
    const { handleClose, handleSubmit, getAvailableTenants } = props;
    const [tenants, setTenants] = useState<any>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedTenant, setSelectedTenant] = useState<any>('');
    const [newName, setNewName] = useState<string>('');
    const [enableCrossTenant, setEnableCrossTenant] = useState<boolean>(false);
    const [targetTenantConfigsNames, setTargetTenantConfigsNames] = useState<any>();
    const [nameIsTaken, setNameIsTaken] = useState<boolean>(false);
    const { authState, oktaAuth } = useOktaAuth();

    const handleTenantSelected = async (e) => {
        const tenant = tenants.find((tenant) => tenant.rowid === e.target.value);
        setSelectedTenant(tenant);
    };

    const handleNameChange = (e) => {
        setNewName(e.target.value);
        checkNameIsAvailable(e.target.value);
    };

    const checkNameIsAvailable = (name) => {
        if (targetTenantConfigsNames?.includes(name)) {
            setNameIsTaken(true);
        } else {
            setNameIsTaken(false);
        }
    };

    const updateTargetTenantConfigsNames = async () => {
        setLoading(true);
        const names = await Backend.getConfigsNames(oktaAuth, selectedTenant.rowid);
        setTargetTenantConfigsNames(names.data);
        setLoading(false);
    };

    const setCrossTenant = async () => {
        const userInfo = await Backend.getUserInfo(oktaAuth);
        const role = userInfo.data.role.name;
        if (role === UserRole.ADMIN) {
            setEnableCrossTenant(true);
        }
    };

    const setUp = async () => {
        setLoading(true);
        setCrossTenant();
        if (getAvailableTenants !== undefined) {
            getAvailableTenants()
                .then((tenants) => {
                    setTenants(tenants.data);
                    const currentTenant =
                        Util.getSessionCurrentTenantInfo() && Util.getSessionCurrentTenantInfo().id
                            ? Util.getSessionCurrentTenantInfo()
                            : Util.getCurrentTenantInfo();
                    const selectedTenantLocal = tenants.data.find((x) => x.rowid === currentTenant.id);
                    if (selectedTenantLocal === undefined) {
                        throw new Error("Can't set up current tenant. Check local storage");
                    }
                    setSelectedTenant(selectedTenantLocal);
                    setLoading(false);
                })
                .catch((error) => {
                    console.error('Error, while getting list of tenants for cloning project:', error);
                    setLoading(false);
                });
        }
    };

    useEffect(() => {
        setUp();
    }, []);

    useEffect(() => {
        if (selectedTenant) {
            updateTargetTenantConfigsNames();
        }
    }, [selectedTenant]);

    useEffect(() => {
        if (newName) {
            checkNameIsAvailable(newName);
        }
    }, [targetTenantConfigsNames]);

    return (
        <div className="CloneDialogContainer">
            <DialogTitle>
                <Stack className="DialogTitle">
                    <Typography className="DialogTitleText">Duplicate project</Typography>
                    <img
                        src={AquantCancelIcon}
                        onClick={() => handleClose()}
                        alt="cancel icon"
                        className="CancelIcon"
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            <Paper className="CloneDialogContentPaper" elevation={0}>
                <DialogContent className="CloneDialogContent">
                    <DialogContentText className="DialogContentTextBold">
                        Enter project details for duplication:
                    </DialogContentText>
                    <Stack className="DetailsContent">
                        <Stack className="DetailsStack">
                            <Stack className="ListdropsStack">
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    marginBottom="20px"
                                >
                                    <Typography className="DialogContentTextNormal">Name your project</Typography>
                                    <TextField
                                        className="InputDialog"
                                        size="small"
                                        label={nameIsTaken ? 'Name is already exist' : 'Type project name'}
                                        value={newName}
                                        error={nameIsTaken}
                                        onChange={handleNameChange}
                                    />
                                </Stack>
                                {enableCrossTenant ? (
                                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                                        <Typography className="DialogContentTextNormal">
                                            Choose where to duplicate
                                        </Typography>

                                        <Select
                                            className={`TenantSelect ${!selectedTenant && 'SelectItalic'}`}
                                            value={selectedTenant ? selectedTenant.rowid : null}
                                            onChange={handleTenantSelected}
                                            displayEmpty
                                            renderValue={(selected) => {
                                                if (selectedTenant === undefined) {
                                                    return <em>{loading ? 'Loading...' : 'Select a customer'}</em>;
                                                }

                                                return tenants.find((tenant) => tenant.rowid === selected)?.tenant_name;
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        borderRadius: '6px',
                                                        boxShadow: '0px 2px 10px rgba(49, 63, 82, 0.211593)',
                                                    },
                                                },
                                                MenuListProps: {
                                                    sx: {
                                                        '& li:hover': {
                                                            background: '#EDEFF5',
                                                        },
                                                        '& li.Mui-selected': {
                                                            fontWeight: 500,
                                                            color: '#1F1F1F',
                                                            background: 'white',
                                                        },
                                                        '& li.Mui-selected:hover': {
                                                            background: '#EDEFF5',
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            {tenants
                                                .sort((a, b) => a.tenant_name.localeCompare(b.tenant_name))
                                                .map((tenant) => (
                                                    <MenuItem key={tenant.rowid} value={tenant.rowid}>
                                                        {tenant.tenant_name}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </Stack>
                                ) : null}
                            </Stack>
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    {loading ? (
                        <CircularProgress data-testid="CircularProgress" sx={{ marginRight: 2 }} size={20} />
                    ) : null}
                    <Button onClick={() => handleClose()} disabled={loading} disableRipple className="DialogActionBtn">
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleSubmit(selectedTenant.rowid, newName)}
                        disabled={loading || !selectedTenant || !newName || nameIsTaken}
                        variant="contained"
                        disableRipple
                        className="DialogActionBtnContained"
                    >
                        Duplicate
                    </Button>
                </DialogActions>
            </Paper>
        </div>
    );
};

export default CloneProjectDialog;
